import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Network, Building2, Users, Wrench, PackageOpen, Gauge, Timer, Calculator, Landmark } from 'lucide-react';
import { ListSection, ListRow, PrimaryButton } from '../../components/RowList';
import SubmitBudgetButton from '../../components/SubmitBudgetButton';

const Manufacturing = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full overflow-y-auto font-sans bg-slate-100 dark:bg-slate-900">
      <div className="max-w-6xl mx-auto px-6 py-10">

        <div className="mb-10 flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Manufacturing</h1>
            <p className="mt-2 text-base text-slate-500 dark:text-slate-400">
              Build up the cost of manufactured items — bills of materials, work centres and factory recoveries.
            </p>
          </div>
          <SubmitBudgetButton module="MOD_3_MAKE" />
        </div>

        <ListSection title="Product Costing">
          <ListRow
            icon={Network}
            title="Bill of Materials"
            description="Define the multi-level BOM per finished good — quantities per, scrap and stage losses."
            actions={<PrimaryButton onClick={() => navigate('/manufacturing/bom')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Gauge}
            title="Work Centre Costing"
            description="Setup and run rates per work centre, setup and run times per item. Hours, batches and cost per unit are derived from production volumes and BOM batch sizes."
            actions={<PrimaryButton onClick={() => navigate('/manufacturing/work-centers-costing')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Calculator}
            title="Manufacturing Costing"
            description="Run the full costing chain — BOM implosion, procurement prices, then the finished-good cost per unit per month. Results and any exceptions on two tabs."
            actions={<PrimaryButton onClick={() => navigate('/manufacturing/costing')}>Open</PrimaryButton>}
          />
        </ListSection>

        <ListSection title="Volumes">
          <ListRow
            icon={PackageOpen}
            title="Production Volumes"
            description="Plan or import production volumes by SKU and site, per month. Edit inline in the budget grid, or round-trip an Excel template (append / overwrite / replace)."
            actions={<PrimaryButton onClick={() => navigate('/manufacturing/production-volumes')}>Open</PrimaryButton>}
          />
        </ListSection>

        <ListSection title="Factory Overheads">
          <ListRow
            icon={Building2}
            title="Fixed Costs"
            description="Baseline fixed factory costs — rent, insurance, rates, leases and depreciation. Manual or Rate × Qty per line."
            actions={<PrimaryButton onClick={() => navigate('/manufacturing/fixed-costs')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Users}
            title="Variable Labour"
            description="Plan the production shift pattern per month — shifts allowed, hours and headcount by timeslot or by date."
            actions={<PrimaryButton onClick={() => navigate('/manufacturing/variable-labour')}>Open</PrimaryButton>}
          />
        </ListSection>

        {/* The asset register these plans hang off lives in Capital — it is the
            same list Capex capitalises against. */}
        <ListSection title="Maintenance">
          <ListRow
            icon={Wrench}
            title="Maintenance Budget"
            description="Budget factory maintenance by line and month — planned, reactive, contracts, spares and shutdowns — each tied to a GL account."
            actions={<PrimaryButton onClick={() => navigate('/manufacturing/maintenance')}>Open</PrimaryButton>}
          />
        </ListSection>

        {/* Budget Setup last: configured once, then rarely revisited. */}
        <ListSection title="Budget Setup">
          <ListRow
            icon={Timer}
            title="Overtime"
            description="Weekend, public-holiday and night rates, the ordinary-hours threshold and the holiday calendar. Rules apply to every site unless a site is named as an exception."
            actions={<PrimaryButton onClick={() => navigate('/manufacturing/overtime')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Landmark}
            title="COGS Posting"
            description="Where material consumption and labour premiums land in the ledger. Two catalogues, most specific rule wins. Base pay is not here — that posts from People › GL Mapping, because a wage is a contract of employment and a shift premium is a production decision."
            actions={<PrimaryButton onClick={() => navigate('/manufacturing/cogs-posting')}>Open</PrimaryButton>}
          />
        </ListSection>

      </div>
    </div>
  );
};

export default Manufacturing;
